import { FC, ReactElement, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from '@packages/utils';
import PageWrapper from 'components/Base/PageWrapper';
import { Tabs } from 'components/Base/Tabs';
import { EmptyView } from 'components/Base/EmptyView';
import { EIconNames } from 'components/Base/Icon';
import { useCheckPermission } from 'hooks/useCheckPermission';
import { EUserType } from 'data/types/user.types';
import { useAppSelector } from 'redux/store';
import { STabsWrapper } from './DuplicateMerge.styles';
import PeopleTab from './tabs/PeopleTab';
import OrganizationsTab from './tabs/OrganizationsTab';
import HistoryTab from './tabs/HistoryTab';

// TODO(prototype): data layer is still mocked (setTimeout / MOCK_*).
// Production tickets T5/T6/T7 will swap mock state for the RTK Query slices
// in `redux/apiSlice/duplicateManagement.slice.ts`. Permission gating is real.

enum EDuplicateMergeTab {
  PEOPLE = 'people',
  ORGANIZATIONS = 'organizations',
  HISTORY = 'history',
}

const TAB_QUERY_PARAM = 'tab';

const isDuplicateMergeTab = (
  value: string | null,
): value is EDuplicateMergeTab =>
  value === EDuplicateMergeTab.PEOPLE ||
  value === EDuplicateMergeTab.ORGANIZATIONS ||
  value === EDuplicateMergeTab.HISTORY;

const DuplicateMerge: FC = () => {
  const { t } = useTranslation({
    keyPrefix: 'descriptions.admin-portal.sidebar',
  });
  const user = useAppSelector(state => state.userSlice?.user);
  const isSuperAdmin = user?.type?.includes(EUserType.SUPER_ADMIN) ?? false;
  const canView =
    useCheckPermission('DUPLICATE_MANAGEMENT_VIEW') || isSuperAdmin;
  const canSearch =
    useCheckPermission('DUPLICATE_MANAGEMENT_SEARCH') || isSuperAdmin;
  const canViewHistory =
    useCheckPermission('DUPLICATE_MANAGEMENT_HISTORY_VIEW') || isSuperAdmin;

  const tabs = useMemo(() => {
    const visible: {
      key: EDuplicateMergeTab;
      label: string;
      node: ReactElement;
    }[] = [];
    if (canView && canSearch) {
      visible.push({
        key: EDuplicateMergeTab.PEOPLE,
        label: 'People',
        node: <PeopleTab />,
      });
      visible.push({
        key: EDuplicateMergeTab.ORGANIZATIONS,
        label: 'Organizations',
        node: <OrganizationsTab />,
      });
    }
    if (canViewHistory) {
      visible.push({
        key: EDuplicateMergeTab.HISTORY,
        label: 'History',
        node: <HistoryTab />,
      });
    }
    return visible;
  }, [canView, canSearch, canViewHistory]);

  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get(TAB_QUERY_PARAM);
  const activeTab: EDuplicateMergeTab = isDuplicateMergeTab(tabFromUrl)
    ? tabFromUrl
    : EDuplicateMergeTab.PEOPLE;

  // Fall back to the first visible tab if the active tab isn't accessible
  // (e.g. URL says `?tab=history` but the user lacks HISTORY_VIEW).
  const effectiveTab = tabs.some(tab => tab.key === activeTab)
    ? activeTab
    : tabs[0]?.key;

  const handleTabChange = (key: string) => {
    const next = new URLSearchParams(searchParams);
    next.set(TAB_QUERY_PARAM, key);
    setSearchParams(next, { replace: true });
  };

  return (
    <PageWrapper title={t('DUPLICATE_MERGE')}>
      <div>
        {tabs.length === 0 ? (
          <div style={{ padding: '0 24px' }}>
            <EmptyView
              alt="no permission"
              emptyText="You do not have permission to view duplicate management."
              icon={EIconNames.EMPTY_DATA}
            />
          </div>
        ) : (
          <STabsWrapper>
            <Tabs
              activeKey={effectiveTab}
              onChange={handleTabChange}
              items={tabs.map(tab => ({
                key: tab.key,
                label: tab.label,
                children: tab.node,
              }))}
            />
          </STabsWrapper>
        )}
      </div>
    </PageWrapper>
  );
};

export default DuplicateMerge;
